import {
  isAdviserAccount,
  isIndividualAccount,
  isLawyerAccount,
  isSingleUserAccount,
  isStandardBusinessAccount
} from '@/dataAccess/accountTypes'
import {
  getCurrentStage,
  getCurrentStageWithoutNegotiation,
  getDocProgressStages,
  getDocProgressStagesWithoutNegotiation
} from '@/utils/progressStages'
import { getValueAtPath } from '@/utils/getValueAtPath'
import { createGroupedLookup, createLookup } from './createLookup'

const millisecondsPerDay = 24 * 60 * 60 * 1000
const now = new Date()

const calcStatus = (client, assignedTo, status, user) => {
  if (status) {
    return status
  }
  if (assignedTo === client) {
    return 'Client'
  }
  if (user && user.accountType !== 'individual') {
    return user.accountType
  }
  return ''
}
const calcProgressStatus = (client, assignedTo, status, user) => {
  if (status === 'Assistance') {
    return 2
  }
  if (assignedTo === client) {
    return 1
  }
  const accountType = getValueAtPath(user, 'accountType')
  if (isAdviserAccount(accountType)) {
    return 2
  }
  if (isLawyerAccount(accountType)) {
    return 3
  }
  return 2
}

const statuses = [
  'Created',
  'Assistance',
  'Editing',
  'Sent to Adviser',
  'Sent to Client',
  'Reassigned',
  'Generated',
  'Downloaded'
]

const calcStatusPriority = (status) => {
  if (status === 'assistance') {
    return 1
  }
  return 2
}

const formatTimestamp = (seconds) => {
  let dateObj = new Date(1970, 0, 1) // Epoch
  dateObj.setSeconds(seconds)
  const month = dateObj.getMonth() + 1
  const year = dateObj.getFullYear()
  const date = dateObj.getDate()

  return `${month}/${date}/${year}`
}

const getters = {
  ...['individual', 'lawyer', 'adviser'].reduce((acc, item) => {
    const propName = 'is' + item[0].toUpperCase() + item.slice(1)
    acc[propName] = (state) => state.claims && state.claims.accountType === item
    return acc
  }, {}),
  appUserLoaded: (state) => state.appUser && 'accountId' in state.appUser,
  accountLoaded: (state) => state.account && 'type' in state.account,
  ...['admin', 'accountAdmin'].reduce((acc, item) => {
    const propName = 'is' + item[0].toUpperCase() + item.slice(1)
    acc[propName] = (state) => state.claims && state.claims[propName]
    return acc
  }, {}),
  isAccountOwner: ({ appUser }, { currentAccountId }) =>
    (appUser.ownerOfAccounts || []).includes(currentAccountId),
  isNonIndividual: (state) =>
    state.claims && !isIndividualAccount(state.claims.accountType),
  isStandardBusiness: (state) =>
    state.claims && isStandardBusinessAccount(state.claims.accountType),
  isSingleUser: (state) =>
    state.claims && isSingleUserAccount(state.claims.accountType),
  is: (state, getters) => {
    var ises = ['user']
    if (!(state.claims && state.claims.accountType)) {
      return ises
    }
    ises.push(state.claims.accountType)
    if (state.claims.isAdmin) {
      ises.push('admin')
    }
    if (state.claims.isAccountAdmin) {
      ises.push('accountAdmin')
    }
    if (getters.isNonIndividual) {
      ises.push('non-individual')
    }
    return ises
  },
  usersById: (state) => {
    /* TODO is there a better way of doing initials? Like storing when name changes */
    return Object.fromEntries(
      Object.entries(state.knownUsers).map(([key, value]) => [
        key,
        {
          ...value,
          initials: value.name
            .split(' ')
            .reduce((acc, word) => acc + word.slice(0, 1), '')
        }
      ])
    )
  },
  accountsById: (state) => {
    return Object.fromEntries(
      Object.entries(state.knownUsers).map(([key, value]) => {
        const { accountId, accountShortName, accountType } = value
        return [value.accountId, { accountId, accountShortName, accountType }]
      })
    )
  },
  docs: (state, getters) => {
    if (state.documentsLoading) {
      return []
    }
    return state.documents
      .filter((doc) => doc.formDef || doc.isDocument)
      .map(processDoc(getters, state))
  },
  dashboardDocs: (state, getters) => {
    if (state.documentsLoading) {
      return []
    }
    return getters.docs.filter((doc) => doc.stage !== 'Vault')
  },
  featureEnabled: ({ globalFeatureToggleSet, userFeatureToggleSet }) => {
    const combinedFeatureTogglesSet = Object.assign(
      {},
      globalFeatureToggleSet,
      userFeatureToggleSet
    )
    return (toggleName) => combinedFeatureTogglesSet[toggleName] || false
  },
  getVaultDocumentById: ({ vaultDocuments }) =>
    createLookup(vaultDocuments, 'vault documents'),
  getActivePricesForProduct: ({ activePrices }) =>
    createGroupedLookup(activePrices, 'product'),
  getAssetsForDocument:
    ({ assets }) =>
      ({ id: documentId }) =>
        assets.filter((asset) => asset.documentId === documentId),
  getVaultDocumentsForAsset:
    (_state, { getVaultDocumentById }) =>
      ({ documentId }) => {
        const doc = getVaultDocumentById(documentId)
        return doc ? [doc] : []
      },
  getAssetById: ({ assets }) => createLookup(assets, 'assets'),

  accountShares: (state) => {
    if (state.sharesLoading) {
      return []
    }
    return state.shares.map((share) => ({
      ...share,
      percentage: `${parseFloat(
        (share.totalOwned / share.noIssued) * 100
      ).toFixed(2)}%`,
      dateIssued: share.dateIssued
        ? formatTimestamp(share.dateIssued.seconds)
        : ''
    }))
  },
  currentAccountId: (state) => {
    return state.selectedAccountId || state.claims.accountId
  }
}

export default getters
function processDoc(getters, state) {
  const withNegotiation = getters.featureEnabled('negTool')
  return (doc) => {
    const assignedToUser = getters.usersById[doc.assignedTo]
    const initiatingAccount = getters.accountsById[doc.accountId]
    const docType = isIndividualAccount(doc.accountType)
      ? 'client'
      : doc.accountType

    const roleUsers = Object.entries(doc.roles || {}).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: getters.usersById[value] }),
      {}
    )
    const userRoles = Object.entries(doc.roles || {}).reduce(
      (acc, [key, value]) => ({ ...acc, [value]: key }),
      {}
    )

    const responsibleUser =
      (doc.roles && docType && roleUsers[docType]) || assignedToUser
    const clientUser = getters.usersById[doc.client]

    let docProgressStages = withNegotiation
      ? getDocProgressStages(doc)
      : getDocProgressStagesWithoutNegotiation(doc)
    const includesLawyer = 'lawyer' in (doc.roles || {})
    const [, progressStageIndex] = withNegotiation
      ? getCurrentStage(doc)
      : getCurrentStageWithoutNegotiation(doc, includesLawyer)

    const progressItems = docProgressStages.map((stage) => ({
      ...stage,
      caption: doc.status,
      progress:
        progressStageIndex === stage.order
          ? doc.progress
          : progressStageIndex > stage.order
            ? 100
            : 0,
      currentStage: progressStageIndex === stage.order
    }))

    const lastActionInfo = ((doc) => {
      const lastActionDate = doc.modified || doc.created
      const lastActionWeeks =
        typeof lastActionDate === 'undefined'
          ? 0
          : Math.trunc(
            Math.abs(now - lastActionDate.toDate()) / millisecondsPerDay / 7
          )

      return {
        lastAction:
          doc.created && doc.modified
            ? doc.created.toDate().toLocaleString() ===
              doc.modified.toDate().toLocaleString()
              ? 'Created'
              : 'Edited'
            : '',
        lastActionDate: doc.modified || doc.created,
        lastActionWeeks: lastActionWeeks,
        lastActionWeeksWarning1: lastActionWeeks >= 4,
        lastActionWeeksWarning2: lastActionWeeks >= 8,
        lastActionWeeksText:
          lastActionWeeks >= 8 ? '8' : lastActionWeeks >= 4 ? '4' : ''
      }
    })(doc)

    const statusIndex =
      statuses.findIndex((status) => status === doc.status) + 1 || 9

    const userHasRole =
      doc.roles && Object.values(doc.roles).includes(state.user.uid)
    const assignedToMe = doc.assignedTo === state.user.uid
    const assignedToClient = doc.assignedTo === doc.client
    const assignedToSameRole =
      userRoles[doc.assignedTo] === state.claims.accountType

    const signed =
      doc.offlineSigned || doc.digitallySigned ? 'Signed' : 'Unsigned'
    const signedSort = ['Unsigned', 'Signed'].indexOf(signed)

    return {
      ...doc,
      id: doc['.key'],
      clientName:
        doc.client === state.user.uid
          ? 'me'
          : clientUser
            ? clientUser.name
            : '',
      accountShortName: initiatingAccount
        ? initiatingAccount.accountId === doc.client
          ? ' '
          : initiatingAccount.accountShortName
        : '',
      businessName: '',
      docTemplateName: doc.name,
      ...lastActionInfo,
      lastActionInfo,
      progress: doc.progress || 50,
      assignedToName: assignedToMe
        ? 'me'
        : assignedToUser
          ? assignedToUser.name
          : '',

      // assignedToId: doc.assignedTo,
      userHasRole: userHasRole,
      assignedToRole: userRoles[doc.assignedTo],
      assignedToSameRole:
        userRoles[doc.assignedTo] === state.claims.accountType,
      assignedToMe: assignedToMe,
      assignedToClient: assignedToClient,
      assignedToSort: [
        Number(!userHasRole),
        Number(!assignedToMe),
        Number(!assignedToSameRole),
        Number(!assignedToClient),
        assignedToUser ? assignedToUser.name : ''
      ].join('-'),
      // assignedToSort: Number(doc.assignedTo !== doc.client) + '-' + Number(doc.roles && !Object.values(doc.roles).includes(state.user.uid)) + '-' + Number(doc.assignedTo !== state.user.uid) + '-' + (assignedToUser ? assignedToUser.name : ''),
      /* assignedToRole: doc.roles && Object.keys(doc.roles).find(role => doc.roles[role] === doc.assignedTo), */
      responsible: responsibleUser && responsibleUser.id,
      responsibleIsMe: responsibleUser && responsibleUser.id === state.user.uid,
      responsibleIsClient: responsibleUser && responsibleUser.id === doc.client,
      responsibleName: responsibleUser && responsibleUser.name,
      // status: this.calcStatus(doc.client, doc.assignedTo, doc.status).toLowerCase() || 'in progress',
      // status: this.calcStatusAlt(doc.status, doc.created, doc.modified, doc.assignedTo).toLowerCase(),
      statusPriority: calcStatusPriority(
        calcStatus(
          doc.client,
          doc.assignedTo,
          doc.status,
          assignedToUser
        ).toLowerCase() || 'in progress'
      ),
      progressStatus: calcProgressStatus(
        doc.client,
        doc.assignedTo,
        doc.status,
        assignedToUser
      ),
      // progressStage: progressStage,
      progressItems: progressItems,
      progressItem: progressItems[progressStageIndex],
      // sorts based on which stage, weighting docs with fewer stages higher, then the status
      stageSort:
        ((progressStageIndex + 1) / progressItems.length) * 100 +
        (1 / progressItems.length) * 10 +
        statusIndex / 100,
      roleUsers: roleUsers,
      nonClientRolesCount: doc.roles
        ? Object.keys(doc.roles).filter((role) => role !== 'client').length
        : 0,
      signed: signed,
      signedSort: signedSort,
      to: {
        name: 'documents',
        params: {
          documentId: doc['.key'],
          clientName: clientUser ? clientUser.name : ''
        }
      }
    }
  }
}
